/**
 * Helpers for PDF viewer compatibility messaging (Safari < 17.4 / missing APIs).
 */

export function isSafariBelowWithResolversSupport(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }

  const ua = navigator.userAgent;
  const isSafari =
    /Safari/i.test(ua) &&
    !/Chrome|Chromium|CriOS|EdgiOS|FXIOS|OPR|Opera/i.test(ua);

  if (!isSafari) {
    return false;
  }

  // Desktop Safari / iOS Safari in many UAs: "Version/17.3"
  const versionMatch = ua.match(/Version\/(\d+)\.(\d+)/);
  if (versionMatch) {
    const major = Number(versionMatch[1]);
    const minor = Number(versionMatch[2]);
    return major < 17 || (major === 17 && minor < 4);
  }

  // iPadOS 13+ desktop UA: look for OS version
  const osMatch = ua.match(/OS (\d+)_(\d+)/);
  if (osMatch) {
    const major = Number(osMatch[1]);
    const minor = Number(osMatch[2]);
    return major < 17 || (major === 17 && minor < 4);
  }

  return false;
}

export function isPromiseWithResolversError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : String(error ?? "");
  return /Promise\.withResolvers is not a function/i.test(message);
}

export function getPdfViewerErrorMessage(error?: unknown): {
  title: string;
  body: string;
} {
  if (
    isPromiseWithResolversError(error) ||
    isSafariBelowWithResolversSupport()
  ) {
    return {
      title: "Update your browser to view this PDF",
      body: "This PDF viewer needs Safari 17.4 or later (or another up-to-date browser). Please update Safari / iPadOS, then try again.",
    };
  }

  return {
    title: "We couldn't display this PDF",
    body: "Sorry, we are having trouble displaying your PDF. Please try again, or contact support if the problem continues.",
  };
}
